import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  X, 
  ShoppingCart, 
  Calendar,
  UtensilsCrossed,
  Coffee,
  Sun,
  Moon,
  UserPlus,
  Flame,
  Beef,
  Wheat,
  Droplet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MealPlanner = () => {
  const { getLocalizedText, language } = useLanguage();
  const { user, isAuthenticated } = useUser();
  const token = localStorage.getItem('love4vegan_user_token');
  
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [mealPlan, setMealPlan] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customText, setCustomText] = useState('');
  const [activeTab, setActiveTab] = useState('recipes');
  const [nutritionSummary, setNutritionSummary] = useState(null);
  const [suggestions, setSuggestions] = useState({ need: null, recipes: [] });

  const getSEOContent = () => {
    const content = {
      en: {
        title: 'Weekly Vegan Meal Planner | love4vegan',
        description: 'Plan your vegan meals for the week. Create shopping lists, track nutrition, and discover new plant-based recipes.'
      },
      fi: {
        title: 'Vegaani Viikkosuunnitelma | love4vegan',
        description: 'Suunnittele viikon vegaaniateriat. Luo kauppalistoja, seuraa ravitsemusta ja löydä uusia kasvisreseptejä.'
      },
      fr: {
        title: 'Planning Repas Végan | love4vegan',
        description: 'Planifiez vos repas végans pour la semaine. Créez des listes de courses et suivez votre nutrition.'
      }
    };
    return content[language] || content.en;
  };

  const seoContent = getSEOContent();

  const texts = {
    title: { fi: 'Viikkosuunnitelma', fr: 'Planning Hebdomadaire', en: 'Weekly Meal Planner' },
    subtitle: { fi: 'Suunnittele viikon ateriat helposti', fr: 'Planifiez vos repas facilement', en: 'Plan your meals for the week' },
    breakfast: { fi: 'Aamiainen', fr: 'Petit-déjeuner', en: 'Breakfast' },
    lunch: { fi: 'Lounas', fr: 'Déjeuner', en: 'Lunch' },
    dinner: { fi: 'Illallinen', fr: 'Dîner', en: 'Dinner' },
    monday: { fi: 'Maanantai', fr: 'Lundi', en: 'Monday' },
    tuesday: { fi: 'Tiistai', fr: 'Mardi', en: 'Tuesday' },
    wednesday: { fi: 'Keskiviikko', fr: 'Mercredi', en: 'Wednesday' },
    thursday: { fi: 'Torstai', fr: 'Jeudi', en: 'Thursday' },
    friday: { fi: 'Perjantai', fr: 'Vendredi', en: 'Friday' },
    saturday: { fi: 'Lauantai', fr: 'Samedi', en: 'Saturday' },
    sunday: { fi: 'Sunnuntai', fr: 'Dimanche', en: 'Sunday' },
    addMeal: { fi: 'Lisää ateria', fr: 'Ajouter un repas', en: 'Add meal' },
    selectRecipe: { fi: 'Valitse resepti', fr: 'Choisir une recette', en: 'Select recipe' },
    customMeal: { fi: 'Oma teksti', fr: 'Texte personnalisé', en: 'Custom text' },
    searchRecipes: { fi: 'Etsi reseptejä...', fr: 'Rechercher des recettes...', en: 'Search recipes...' },
    customPlaceholder: { fi: 'esim. Tähteet, Ravintolassa...', fr: 'ex. Restes, Restaurant...', en: 'e.g. Leftovers, Eating out...' },
    add: { fi: 'Lisää', fr: 'Ajouter', en: 'Add' },
    addToShoppingList: { fi: 'Lisää ainekset kauppalistaan', fr: 'Ajouter au panier', en: 'Add ingredients to shopping list' },
    signUpPrompt: { fi: 'Kirjaudu sisään tallentaaksesi suunnitelmasi!', fr: 'Connectez-vous pour sauvegarder!', en: 'Sign in to save your meal plan!' },
    signUp: { fi: 'Kirjaudu / Rekisteröidy', fr: 'Connexion / Inscription', en: 'Sign In / Sign Up' },
    thisWeek: { fi: 'Tällä viikolla', fr: 'Cette semaine', en: 'This week' },
    saved: { fi: 'Tallennettu!', fr: 'Sauvegardé!', en: 'Saved!' },
    ingredientsAdded: { fi: 'Ainekset lisätty kauppalistaan!', fr: 'Ingrédients ajoutés!', en: 'Ingredients added to shopping list!' },
    nutritionSummary: { fi: 'Viikon ravintotiedot', fr: 'Résumé nutritionnel', en: 'Weekly Nutrition Summary' },
    calories: { fi: 'Kalorit', fr: 'Calories', en: 'Calories' },
    protein: { fi: 'Proteiini', fr: 'Protéines', en: 'Protein' },
    carbs: { fi: 'Hiilihydraatit', fr: 'Glucides', en: 'Carbs' },
    fat: { fi: 'Rasva', fr: 'Lipides', en: 'Fat' },
    fiber: { fi: 'Kuitu', fr: 'Fibres', en: 'Fiber' },
    dailyAvg: { fi: 'Päivittäin (keskim.)', fr: 'Moyenne quotidienne', en: 'Daily average' },
    weeklyTotal: { fi: 'Viikko yhteensä', fr: 'Total semaine', en: 'Weekly total' },
    nutritionTips: { fi: 'Ravintovinkit', fr: 'Conseils nutrition', en: 'Nutrition Tips' },
    lowProtein: { fi: 'Proteiini vähäinen! Kokeile näitä:', fr: 'Protéines faibles! Essayez:', en: 'Protein is low! Try these:' },
    lowFiber: { fi: 'Kuitu vähäinen! Kokeile näitä:', fr: 'Fibres faibles! Essayez:', en: 'Fiber is low! Try these:' },
    goodBalance: { fi: 'Hyvä tasapaino! 💚', fr: 'Bon équilibre! 💚', en: 'Good balance! 💚' },
    addToMeal: { fi: 'Lisää', fr: 'Ajouter', en: 'Add' }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const meals = ['breakfast', 'lunch', 'dinner'];

  const mealIcons = {
    breakfast: Coffee,
    lunch: Sun,
    dinner: Moon
  };

  // Get Monday of current week
  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Format date for display
  function formatDate(date) {
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short'
    });
  }

  // Get date for a specific day of the week
  function getDayDate(dayIndex) {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayIndex);
    return date;
  }

  // Navigate weeks
  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const goToThisWeek = () => {
    setCurrentWeekStart(getMonday(new Date()));
  };

  // Fetch recipes
  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/recipes`);
        setRecipes(response.data);
      } catch (error) {
        console.error('Error fetching recipes:', error);
      }
    };
    fetchRecipes();
  }, []);

  // Fetch meal plan when week changes
  useEffect(() => {
    const fetchMealPlan = async () => {
      if (!isAuthenticated || !token) {
        setMealPlan(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const weekStartStr = currentWeekStart.toISOString().split('T')[0];
        const response = await axios.get(`${API_URL}/api/meal-plan?week_start=${weekStartStr}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMealPlan(response.data);
      } catch (error) {
        console.error('Error fetching meal plan:', error);
        setMealPlan(null);
      } finally {
        setLoading(false);
      }
    };
    fetchMealPlan();
  }, [currentWeekStart, isAuthenticated, token]);

  // Fetch nutrition summary when meal plan changes
  useEffect(() => {
    const fetchNutrition = async () => {
      if (!isAuthenticated || !token || !mealPlan) {
        setNutritionSummary(null);
        return;
      }

      try {
        const weekStartStr = currentWeekStart.toISOString().split('T')[0];
        const response = await axios.get(`${API_URL}/api/meal-plan/nutrition-summary?week_start=${weekStartStr}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setNutritionSummary(response.data);
      } catch (error) {
        console.error('Error fetching nutrition:', error);
      }
    };
    fetchNutrition();
  }, [mealPlan, currentWeekStart, isAuthenticated, token]);

  // Fetch nutrition-based recipe suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!nutritionSummary || !nutritionSummary.daily_average) {
        setSuggestions({ need: null, recipes: [] });
        return;
      }

      const dailyAvg = nutritionSummary.daily_average;
      
      // Determine what nutrient is lacking (rough daily targets for vegan diet)
      // Protein: ~50g/day, Fiber: ~25g/day
      let need = null;
      if (dailyAvg.protein < 15) {
        need = 'protein';
      } else if (dailyAvg.fiber < 8) {
        need = 'fiber';
      }

      if (need) {
        try {
          const response = await axios.get(`${API_URL}/api/recipes/suggestions/nutrition?need=${need}&limit=3`);
          setSuggestions({ need, recipes: response.data.suggestions || [] });
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      } else {
        setSuggestions({ need: 'balanced', recipes: [] });
      }
    };
    fetchSuggestions();
  }, [nutritionSummary]);

  // Save meal plan
  const saveMealPlan = async (updatedPlan) => {
    if (!isAuthenticated || !token) return;

    try {
      const weekStartStr = currentWeekStart.toISOString().split('T')[0];
      await axios.put(`${API_URL}/api/meal-plan`, {
        week_start: weekStartStr,
        ...updatedPlan
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(getLocalizedText(texts.saved));
      
      // Refresh nutrition after saving
      setTimeout(async () => {
        try {
          const response = await axios.get(`${API_URL}/api/meal-plan/nutrition-summary?week_start=${weekStartStr}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setNutritionSummary(response.data);
        } catch (e) {}
      }, 500);
    } catch (error) {
      console.error('Error saving meal plan:', error);
      toast.error('Error saving');
    }
  };

  // Open dialog to add meal
  const openAddMealDialog = (day, meal) => {
    setSelectedSlot({ day, meal });
    setSearchQuery('');
    setCustomText('');
    setActiveTab('recipes');
    setDialogOpen(true);
  };

  // Add recipe to slot
  const addRecipeToSlot = (recipe) => {
    if (!selectedSlot) return;

    const updatedPlan = {
      ...mealPlan,
      [selectedSlot.day]: {
        ...mealPlan?.[selectedSlot.day],
        [selectedSlot.meal]: {
          type: 'recipe',
          recipe_id: recipe.id,
          custom_text: null
        }
      }
    };

    setMealPlan(updatedPlan);
    saveMealPlan(updatedPlan);
    setDialogOpen(false);
  };

  // Add custom text to slot
  const addCustomToSlot = () => {
    if (!selectedSlot || !customText.trim()) return;

    const updatedPlan = {
      ...mealPlan,
      [selectedSlot.day]: {
        ...mealPlan?.[selectedSlot.day],
        [selectedSlot.meal]: {
          type: 'custom',
          recipe_id: null,
          custom_text: customText.trim()
        }
      }
    };

    setMealPlan(updatedPlan);
    saveMealPlan(updatedPlan);
    setDialogOpen(false);
  };

  // Remove meal from slot
  const removeMealFromSlot = (day, meal) => {
    const updatedPlan = {
      ...mealPlan,
      [day]: {
        ...mealPlan?.[day],
        [meal]: null
      }
    };

    setMealPlan(updatedPlan);
    saveMealPlan(updatedPlan);
  };

  // Get recipe by ID
  const getRecipeById = (recipeId) => {
    return recipes.find(r => r.id === recipeId);
  };

  // Add all ingredients to shopping list
  const addAllToShoppingList = async () => {
    if (!isAuthenticated || !token) return;

    try {
      const weekStartStr = currentWeekStart.toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/api/meal-plan/ingredients?week_start=${weekStartStr}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const ingredients = response.data.ingredients;
      
      // Get existing shopping list from localStorage
      const existingList = JSON.parse(localStorage.getItem('love4vegan_shopping_list') || '[]');
      
      // Add ingredients from each recipe
      Object.values(ingredients).forEach(recipeData => {
        const recipeIngredients = recipeData.ingredients?.[language] || recipeData.ingredients?.fi || '';
        const lines = recipeIngredients.split('\n').filter(line => line.trim());
        lines.forEach(line => {
          const cleanLine = line.replace(/^[-•*]\s*/, '').trim();
          if (cleanLine && !existingList.includes(cleanLine)) {
            existingList.push(cleanLine);
          }
        });
      });

      localStorage.setItem('love4vegan_shopping_list', JSON.stringify(existingList));
      toast.success(getLocalizedText(texts.ingredientsAdded));
    } catch (error) {
      console.error('Error getting ingredients:', error);
      toast.error('Error');
    }
  };

  // Find first empty slot in meal plan
  const findFirstEmptySlot = () => {
    for (const day of days) {
      for (const meal of meals) {
        const mealData = mealPlan?.[day]?.[meal];
        if (!mealData) {
          return { day, meal };
        }
      }
    }
    return null;
  };

  // Add suggested recipe to first empty slot
  const addSuggestedRecipe = (recipeId) => {
    const emptySlot = findFirstEmptySlot();
    if (emptySlot) {
      const updatedPlan = {
        ...mealPlan,
        [emptySlot.day]: {
          ...mealPlan?.[emptySlot.day],
          [emptySlot.meal]: {
            type: 'recipe',
            recipe_id: recipeId,
            custom_text: null
          }
        }
      };
      setMealPlan(updatedPlan);
      saveMealPlan(updatedPlan);
      toast.success(getLocalizedText({ 
        fi: `Lisätty: ${emptySlot.day} ${emptySlot.meal}`, 
        fr: `Ajouté: ${emptySlot.day} ${emptySlot.meal}`, 
        en: `Added to ${emptySlot.day} ${emptySlot.meal}` 
      }));
    } else {
      toast.error(getLocalizedText({ 
        fi: 'Ei tyhjiä paikkoja', 
        fr: 'Pas de place libre', 
        en: 'No empty slots available' 
      }));
    }
  };

  // Filter recipes by search
  const filteredRecipes = recipes.filter(recipe => {
    const title = recipe.title?.[language] || recipe.title?.fi || '';
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Render meal slot content
  const renderMealSlot = (day, meal) => {
    const mealData = mealPlan?.[day]?.[meal];
    
    if (!mealData) {
      return (
        <button
          onClick={() => isAuthenticated ? openAddMealDialog(day, meal) : null}
          className={`w-full h-full min-h-[60px] border-2 border-dashed border-border/50 rounded-lg flex items-center justify-center text-foreground/40 hover:border-primary/50 hover:text-primary/50 transition-colors ${!isAuthenticated ? 'cursor-not-allowed opacity-50' : ''}`}
          data-testid={`add-meal-${day}-${meal}`}
        >
          <Plus className="h-5 w-5" />
        </button>
      );
    }

    if (mealData.type === 'recipe') {
      const recipe = getRecipeById(mealData.recipe_id);
      if (!recipe) return null;
      
      const title = recipe.title?.[language] || recipe.title?.fi || 'Recipe';
      const image = recipe.image_data || recipe.image_url;

      return (
        <div className="relative group w-full h-full min-h-[60px] bg-primary/5 rounded-lg overflow-hidden">
          {image && (
            <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
          )}
          <div className="relative p-2 h-full flex items-center">
            <Link to={`/recipes/${recipe.id}`} className="text-sm font-medium text-foreground hover:text-primary line-clamp-2">
              {title}
            </Link>
          </div>
          <button
            onClick={() => removeMealFromSlot(day, meal)}
            className="absolute top-1 right-1 p-1 bg-destructive/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`remove-meal-${day}-${meal}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }

    if (mealData.type === 'custom') {
      return (
        <div className="relative group w-full h-full min-h-[60px] bg-secondary/20 rounded-lg p-2 flex items-center">
          <span className="text-sm text-foreground/70 italic line-clamp-2">{mealData.custom_text}</span>
          <button
            onClick={() => removeMealFromSlot(day, meal)}
            className="absolute top-1 right-1 p-1 bg-destructive/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="meal-planner-page">
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        image="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200"
        url="https://love4vegan.com/meal-planner"
        type="website"
        keywords="vegan meal planner, meal prep, vegaani viikkosuunnitelma, planning repas végan"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold font-serif text-primary">
              {getLocalizedText(texts.title)}
            </h1>
          </div>
          <p className="text-foreground/60">{getLocalizedText(texts.subtitle)}</p>
        </div>

        {/* Sign up prompt for non-authenticated users */}
        {!isAuthenticated && (
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 mb-8 text-center">
            <UserPlus className="h-12 w-12 text-primary mx-auto mb-3" />
            <p className="text-lg font-medium mb-4">{getLocalizedText(texts.signUpPrompt)}</p>
            <Link to="/auth">
              <Button className="bg-primary hover:bg-primary/90">
                {getLocalizedText(texts.signUp)}
              </Button>
            </Link>
          </div>
        )}

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6 bg-card rounded-xl p-4 border border-border">
          <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-4">
            <span className="text-lg font-medium">
              {formatDate(currentWeekStart)} - {formatDate(getDayDate(6))}
            </span>
            <Button variant="ghost" size="sm" onClick={goToThisWeek}>
              {getLocalizedText(texts.thisWeek)}
            </Button>
          </div>
          
          <Button variant="outline" size="icon" onClick={goToNextWeek}>
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Add to Shopping List Button */}
        {isAuthenticated && (
          <div className="flex justify-end mb-4">
            <Button 
              onClick={addAllToShoppingList}
              variant="outline"
              className="gap-2"
              data-testid="add-all-to-shopping-list"
            >
              <ShoppingCart className="h-4 w-4" />
              {getLocalizedText(texts.addToShoppingList)}
            </Button>
          </div>
        )}

        {/* Meal Plan Grid */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {/* Header row with meal types */}
          <div className="grid grid-cols-4 bg-muted/50">
            <div className="p-3 border-r border-border"></div>
            {meals.map(meal => {
              const Icon = mealIcons[meal];
              return (
                <div key={meal} className="p-3 text-center border-r border-border last:border-r-0">
                  <div className="flex items-center justify-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">{getLocalizedText(texts[meal])}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Days rows */}
          {days.map((day, dayIndex) => (
            <div key={day} className="grid grid-cols-4 border-t border-border">
              {/* Day label */}
              <div className="p-3 border-r border-border bg-muted/30">
                <div className="font-medium">{getLocalizedText(texts[day])}</div>
                <div className="text-xs text-foreground/50">{formatDate(getDayDate(dayIndex))}</div>
              </div>
              
              {/* Meal slots */}
              {meals.map(meal => (
                <div key={meal} className="p-2 border-r border-border last:border-r-0">
                  {renderMealSlot(day, meal)}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Nutrition Summary */}
        {isAuthenticated && nutritionSummary && (nutritionSummary.weekly_total?.calories > 0) && (
          <div className="mt-8 bg-card rounded-xl border border-border p-6" data-testid="nutrition-summary">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Flame className="h-5 w-5 text-primary" />
              {getLocalizedText(texts.nutritionSummary)}
            </h2>
            
            {/* Weekly Total & Daily Average */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Daily Average */}
              <div className="bg-muted/30 rounded-xl p-4">
                <h3 className="text-sm font-medium text-foreground/60 mb-3">{getLocalizedText(texts.dailyAvg)}</h3>
                <div className="grid grid-cols-5 gap-2">
                  <div className="text-center">
                    <div className="flex items-center justify-center w-10 h-10 mx-auto mb-1 bg-orange-100 dark:bg-orange-950/50 rounded-full">
                      <Flame className="h-4 w-4 text-orange-500" />
                    </div>
                    <p className="font-bold text-lg">{nutritionSummary.daily_average?.calories || 0}</p>
                    <p className="text-xs text-foreground/50">kcal</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-10 h-10 mx-auto mb-1 bg-red-100 dark:bg-red-950/50 rounded-full">
                      <Beef className="h-4 w-4 text-red-500" />
                    </div>
                    <p className="font-bold text-lg">{nutritionSummary.daily_average?.protein || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.protein)}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-10 h-10 mx-auto mb-1 bg-amber-100 dark:bg-amber-950/50 rounded-full">
                      <Wheat className="h-4 w-4 text-amber-500" />
                    </div>
                    <p className="font-bold text-lg">{nutritionSummary.daily_average?.carbs || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.carbs)}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-10 h-10 mx-auto mb-1 bg-blue-100 dark:bg-blue-950/50 rounded-full">
                      <Droplet className="h-4 w-4 text-blue-500" />
                    </div>
                    <p className="font-bold text-lg">{nutritionSummary.daily_average?.fat || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.fat)}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-10 h-10 mx-auto mb-1 bg-green-100 dark:bg-green-950/50 rounded-full">
                      <Wheat className="h-4 w-4 text-green-500" />
                    </div>
                    <p className="font-bold text-lg">{nutritionSummary.daily_average?.fiber || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.fiber)}</p>
                  </div>
                </div>
              </div>

              {/* Weekly Total */}
              <div className="bg-primary/5 rounded-xl p-4">
                <h3 className="text-sm font-medium text-foreground/60 mb-3">{getLocalizedText(texts.weeklyTotal)}</h3>
                <div className="grid grid-cols-5 gap-2">
                  <div className="text-center">
                    <p className="font-bold text-lg text-primary">{nutritionSummary.weekly_total?.calories || 0}</p>
                    <p className="text-xs text-foreground/50">kcal</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg text-primary">{nutritionSummary.weekly_total?.protein || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.protein)}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg text-primary">{nutritionSummary.weekly_total?.carbs || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.carbs)}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg text-primary">{nutritionSummary.weekly_total?.fat || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.fat)}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg text-primary">{nutritionSummary.weekly_total?.fiber || 0}g</p>
                    <p className="text-xs text-foreground/50">{getLocalizedText(texts.fiber)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Note about estimates */}
            <p className="text-xs text-foreground/40 text-center">
              {getLocalizedText({ 
                fi: '* Ravintotiedot ovat arvioita ja perustuvat reseptien raaka-aineisiin', 
                fr: '* Les informations nutritionnelles sont des estimations basées sur les ingrédients', 
                en: '* Nutrition values are estimates based on recipe ingredients' 
              })}
            </p>
          </div>
        )}

        {/* Nutrition Tips - Simple Version */}
        {isAuthenticated && suggestions.need === 'protein' && (
          <div className="mt-6 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-900 p-4" data-testid="nutrition-tips">
            <p className="text-amber-700 dark:text-amber-300 font-medium mb-2">
              💡 {getLocalizedText(texts.lowProtein)}
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {getLocalizedText({ 
                fi: 'Lisää proteiinia linssisalaatilla, Buddha Bowlilla tai tofuruoalla!', 
                fr: 'Ajoutez des protéines avec une salade de lentilles, un Buddha Bowl ou un plat de tofu!', 
                en: 'Add protein with lentil salad, Buddha Bowl, or tofu dishes!' 
              })}
            </p>
          </div>
        )}
        
        {isAuthenticated && suggestions.need === 'fiber' && (
          <div className="mt-6 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-900 p-4" data-testid="nutrition-tips">
            <p className="text-amber-700 dark:text-amber-300 font-medium mb-2">
              💡 {getLocalizedText(texts.lowFiber)}
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {getLocalizedText({ 
                fi: 'Lisää kuitua täysjyväviljalla, palkokasveilla ja vihanneksilla!', 
                fr: 'Ajoutez des fibres avec des grains entiers, des légumineuses et des légumes!', 
                en: 'Add fiber with whole grains, legumes, and vegetables!' 
              })}
            </p>
          </div>
        )}

        {isAuthenticated && suggestions.need === 'balanced' && (
          <div className="mt-6 bg-green-50 dark:bg-green-950/20 rounded-xl border border-green-200 dark:border-green-900 p-4 text-center" data-testid="nutrition-balanced">
            <p className="text-green-700 dark:text-green-300 font-medium">
              {getLocalizedText(texts.goodBalance)}
            </p>
          </div>
        )}

        {/* Add Meal Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5 text-primary" />
                {getLocalizedText(texts.addMeal)}
              </DialogTitle>
            </DialogHeader>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border pb-3">
              <Button
                variant={activeTab === 'recipes' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('recipes')}
              >
                {getLocalizedText(texts.selectRecipe)}
              </Button>
              <Button
                variant={activeTab === 'custom' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('custom')}
              >
                {getLocalizedText(texts.customMeal)}
              </Button>
            </div>

            {/* Recipe selection */}
            {activeTab === 'recipes' && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <Input
                  placeholder={getLocalizedText(texts.searchRecipes)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-3"
                  data-testid="recipe-search-input"
                />
                <div className="flex-1 overflow-y-auto space-y-2">
                  {filteredRecipes.map(recipe => {
                    const title = recipe.title?.[language] || recipe.title?.fi || 'Recipe';
                    const image = recipe.image_data || recipe.image_url;
                    
                    return (
                      <button
                        key={recipe.id}
                        onClick={() => addRecipeToSlot(recipe)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                        data-testid={`select-recipe-${recipe.id}`}
                      >
                        {image && (
                          <img src={image} alt={title} className="w-12 h-12 rounded object-cover" />
                        )}
                        <span className="font-medium">{title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom text input */}
            {activeTab === 'custom' && (
              <div className="space-y-4">
                <Input
                  placeholder={getLocalizedText(texts.customPlaceholder)}
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  data-testid="custom-meal-input"
                />
                <Button 
                  onClick={addCustomToSlot}
                  disabled={!customText.trim()}
                  className="w-full"
                  data-testid="add-custom-meal-btn"
                >
                  {getLocalizedText(texts.add)}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
